import { useState } from "react";
import { useAdvanceWizardStep } from "../../hooks/use-wizard.js";
import { Button } from "../../components/button.js";
import { FormField } from "../../components/form-field.js";
import type { WizardState, OrgType } from "../../api/types.js";
import type { Organisation } from "../../api/types.js";

const ORG_TYPE_LABELS: Record<OrgType, string> = {
  public_body: "Public body / government",
  private_company: "Private company",
  non_profit: "Non-profit / charity",
  educational: "Educational institution",
  healthcare: "Healthcare provider",
  other: "Other",
};

/** Simple email validation regex */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

interface StatementSetupStepProps {
  websiteId: string;
  wizardState: WizardState;
  org: Organisation | null;
}

export function StatementSetupStep({
  websiteId,
  wizardState,
  org,
}: StatementSetupStepProps) {
  const stmtData = wizardState.steps.find(
    (s) => s.step === "statement_setup",
  )?.data;

  const [orgName, setOrgName] = useState(
    (stmtData?.orgName as string | undefined) ?? org?.name ?? "",
  );
  const [contactEmail, setContactEmail] = useState(
    (stmtData?.accessibilityContactEmail as string | undefined) ??
      org?.accessibilityContactEmail ??
      "",
  );
  const [emailError, setEmailError] = useState("");
  const [orgNameError, setOrgNameError] = useState("");
  const [orgType, setOrgType] = useState<OrgType>(
    (stmtData?.orgType as OrgType | undefined) ??
      org?.type ??
      "private_company",
  );

  const advance = useAdvanceWizardStep(websiteId);

  function validateEmail(email: string) {
    if (!email.trim()) {
      setEmailError("Email is required");
    } else if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address");
    } else {
      setEmailError("");
    }
  }

  function handleEmailChange(value: string) {
    setContactEmail(value);
    if (emailError) {
      validateEmail(value);
    }
  }

  function handleEmailBlur() {
    validateEmail(contactEmail);
  }

  function handleContinue(e: React.FormEvent) {
    e.preventDefault();

    if (!orgName.trim()) {
      setOrgNameError("Organisation name is required");
      return;
    }

    validateEmail(contactEmail);
    if (!isValidEmail(contactEmail)) {
      return;
    }

    advance.mutate({
      step: "statement_setup",
      outcome: "pass",
      data: {
        orgName,
        accessibilityContactEmail: contactEmail,
        orgType,
      },
    });
  }

  const isFormValid = orgName.trim() && isValidEmail(contactEmail);

  return (
    <div className="wizard-content">
      <h2 className="wizard-content__title">Statement setup</h2>
      <p className="wizard-content__subtitle">
        This information will appear in your accessibility statement.
      </p>

      {advance.error && (
        <div className="alert alert--error">{advance.error.message}</div>
      )}

      <form onSubmit={handleContinue} noValidate>
        <FormField id="orgName" label="Organisation name" required error={orgNameError}>
          <input
            id="orgName"
            className="form-field__input"
            value={orgName}
            onChange={(e) => {
              setOrgName(e.target.value);
              if (orgNameError) setOrgNameError("");
            }}
            onBlur={() => {
              if (!orgName.trim()) setOrgNameError("Organisation name is required");
            }}
            aria-invalid={!!orgNameError}
            aria-describedby={orgNameError ? "orgName-error" : undefined}
            required
          />
          {orgNameError && (
            <div id="orgName-error" className="form-field__error">
              {orgNameError}
            </div>
          )}
        </FormField>

        <FormField id="orgType" label="Organisation type" required>
          <select
            id="orgType"
            className="form-field__select"
            value={orgType}
            onChange={(e) => setOrgType(e.target.value as OrgType)}
          >
            {(Object.keys(ORG_TYPE_LABELS) as OrgType[]).map((t) => (
              <option key={t} value={t}>
                {ORG_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          id="contactEmail"
          label="Accessibility contact email"
          required
          hint="Published in your accessibility statement for users to report issues."
          error={emailError}
        >
          <input
            id="contactEmail"
            type="email"
            className="form-field__input"
            value={contactEmail}
            onChange={(e) => handleEmailChange(e.target.value)}
            onBlur={handleEmailBlur}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? "contactEmail-error" : undefined}
            required
          />
          {emailError && (
            <div id="contactEmail-error" className="form-field__error">
              {emailError}
            </div>
          )}
        </FormField>

        {advance.error && (
          <div className="alert alert--error mt-3">{advance.error.message}</div>
        )}

        <div className="wizard-content__actions">
          <Button
            type="submit"
            loading={advance.isPending}
            disabled={!isFormValid}
          >
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
}
